// src/components/Admin/QuestionAsked.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import QuestionsContainer from './QuestionsContainer';
import PopupWrapper from '../../components/UI/PopupWrapper';
import AnswerQuestionModal from '../../components/Modals/AnswerQuestionModal';
import ViewAnswerModal from '../../components/Modals/ViewAnswerModal';
import AskedPopup from '../../components/UI/AskedPopup';

const QuestionAsked = () => {
  const { currentUser } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [activePopupId, setActivePopupId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load only non-curated questions for the logged-in user on mount.
  useEffect(() => {
    fetchQuestions();
  }, [currentUser]);

  const fetchQuestions = () => {
    fetch('/api/questions')
      .then(res => res.json())
      .then(data => {
        console.log("All fetched questions:", data);
        // Filter out curated questions.
        let filtered = (data || [])
          .filter(q => !q.isCurated)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        if (currentUser && currentUser.username) {
          console.log("Current user username:", currentUser.username);
          // Include questions where:
          // - The question was asked by this user (by username), or
          // - It was asked as a guest and the askedEmail matches the current user's email.
          filtered = filtered.filter(q => {
            return (
              q.fromUsername === currentUser.username ||
              (q.fromUsername === 'guest' &&
                q.askedEmail.toLowerCase() === currentUser.email.toLowerCase())
            );
          });
        }
        console.log("Filtered questions for QuestionAsked:", filtered);
        setQuestions(filtered);
      })
      .catch(err => {
        console.error("Error fetching questions:", err);
      });
  };

  const handleDelete = async (id) => {
    try {
      // Fetch the question data before proceeding with the deletion
      const questionResponse = await fetch(`/api/questions/${id}`);
      if (!questionResponse.ok) {
        console.error(`Failed to fetch question with ID ${id}`);
        return;
      }

      const questionData = await questionResponse.json();

      if (!questionData) {
        console.error(`Failed to fetch data for question with ID ${id}`);
        return;
      }

      // If there's a tip (greater than 0), cancel the payment
      if (questionData.tip && questionData.tip > 0) {
        const cancelResponse = await fetch(`/api/payments/process/${id}/cancel-payment`, {
          method: 'POST',
        });

        const cancelResponseBody = await cancelResponse.json();
        console.log('Cancel payment response:', cancelResponseBody);

        if (!cancelResponse.ok) {
          console.error("Failed to cancel payment for question:", id);
          return; // If payment cancellation fails, stop further execution
        } else {
          console.log(`Payment cancelled for question ${id}`);
        }
      } else {
        console.log(`No tip for question ${id}, skipping cancel payment.`);
      }

      // Proceed with deleting the question
      const deleteResponse = await fetch(`/api/questions/${id}`, {
        method: 'DELETE',
      });

      if (deleteResponse.ok) {
        // Remove the question from the state after successful deletion
        setQuestions(prevQuestions => prevQuestions.filter(q => q._id !== id));
        console.log(`Question ${id} deleted successfully`);
      } else {
        console.error("Failed to delete question:", id);
      }
    } catch (err) {
      console.error('Error during delete process:', err);
    }
  };

  const handleViewAnswer = (id, questionText, detail, answer) => {
    console.log("Viewing answer for question:", id);
    setSelectedQuestion({ _id: id, question: questionText, detail, answer });
    setActivePopupId(null);
    setConfirmDeleteId(null);
  };

  // Added function to update question after an answer is posted
  const handleAnswerPosted = (updatedQuestion) => {
    setQuestions(prev =>
      prev.map(q =>
        q._id.toString() === updatedQuestion._id.toString() ? updatedQuestion : q
      )
    );
    setSelectedQuestion(null);
  };

  // Filter questions based on search term.
  const filteredQuestions = questions.filter(q => {
    const search = searchTerm.toLowerCase();
    const questionText = q.question.toLowerCase();
    const dateString = new Date(q.createdAt).toLocaleDateString().toLowerCase();
    // For asked questions, include the recipient info.
    let recipient = '';
    if(q.toUsername) recipient += q.toUsername.toLowerCase();
    return questionText.includes(search) || dateString.includes(search) || recipient.includes(search);
  });

  const tableHead = (
    <tr>
      <th>Question</th>
      <th>Asked To</th>
      <th>Date</th>
      <th>Tip</th>
      <th>Tag</th>
      <th>Actions</th>
    </tr>
  );

  const renderDesktopRow = (q, index) => {
    const qId = q._id ? q._id.toString() : "";
    return (
      <tr key={qId} className={index === 0 ? 'highlight' : ''}>
        <td>{q.question}</td>
        <td>{q.recipientName || q.toUsername || "N/A"}</td>
        <td>{new Date(q.createdAt).toLocaleDateString()}</td>
        <td>{q.tip ? `$${q.tip}` : '$0'}</td>
        <td>
          <span className={`tag ${q.status === "Pending" ? "inactive" : ""}`}>
            {q.status}
          </span>
        </td>
        <td className="table-action">
          {q.status !== "Pending" && (
            <button
              className="table-btn action-button"
              onClick={() => handleViewAnswer(q._id, q.question, q.detail, q.answer)}
            >
              <img src="/images/i_table_view.svg" alt="View answer" />
            </button>
          )}
          <PopupWrapper onClose={() => { setActivePopupId(null); setConfirmDeleteId(null); }}>
            <>
              <button
                className="table-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log("Toggling popup for question:", q._id);
                  setActivePopupId(q._id);
                  setConfirmDeleteId(null);
                }}
              >
                <img className="more" src="/images/i_More.svg" alt="More" />
              </button>
              {activePopupId === q._id && (
                <AskedPopup
                  q={q}
                  onDelete={q.status === "Pending" ? () => handleDelete(q._id) : null}
                  onView={q.status !== "Pending" ? () => handleViewAnswer(q._id, q.question, q.detail, q.answer) : null}
                  onClose={() => { setActivePopupId(null); setConfirmDeleteId(null); }}
                  confirm={confirmDeleteId === q._id}
                  onOpenConfirm={() => {
                    console.log("Opening confirm delete popup for question:", q._id);
                    setConfirmDeleteId(q._id);
                  }}
                  showLabel="View answer"
                />
              )}
            </>
          </PopupWrapper>
        </td>
      </tr>
    );
  };

  const renderMobileCard = (q) => {
    const qId = q._id ? q._id.toString() : "";
    return (
      <div className="card" key={qId} style={{ position: 'relative' }}>
        <p className="xlarge">{q.question}</p>
        <span className="tag">{q.tip ? `$${q.tip}` : '$0'}</span>
        {q.status !== "Pending" && (
          <button
            className="grey full-width"
            onClick={() => handleViewAnswer(q._id, q.question, q.detail, q.answer)}
          >
            <img src="/images/i_View_Answer.svg" alt="View answer" />
          </button>
        )}
        <PopupWrapper onClose={() => { setActivePopupId(null); setConfirmDeleteId(null); }}>
          <>
            <button
              className="clear full-width"
              onClick={(e) => {
                e.stopPropagation();
                console.log("Mobile popup toggled for question:", q._id);
                setActivePopupId(q._id);
                setConfirmDeleteId(null);
              }}
            >
              <img src="/images/i_More.svg" alt="More" />
            </button>
            {activePopupId === q._id && (
              <AskedPopup
                q={q}
                onDelete={q.status === "Pending" ? () => handleDelete(q._id) : null}
                onView={q.status !== "Pending" ? () => handleViewAnswer(q._id, q.question, q.detail, q.answer) : null}
                onClose={() => { setActivePopupId(null); setConfirmDeleteId(null); }}
                confirm={confirmDeleteId === q._id}
                onOpenConfirm={() => {
                  console.log("Mobile: Opening confirm delete popup for question:", q._id);
                  setConfirmDeleteId(q._id);
                }}
                showLabel="View answer"
              />
            )}
          </>
        </PopupWrapper>
      </div>
    );
  };

  return (
    <>
      <div className="admin-action-header">
        <div className="search-container">
          <input 
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <QuestionsContainer
        questions={filteredQuestions}
        renderDesktopRow={renderDesktopRow}
        renderMobileCard={renderMobileCard}
        tableHead={tableHead}
      />
      {selectedQuestion && (
        selectedQuestion.answer ? (
          <ViewAnswerModal
            onClose={() => setSelectedQuestion(null)}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            answer={selectedQuestion.answer}
            questionId={selectedQuestion._id}
            onAnswerPosted={handleAnswerPosted}
            editable={false} // disable editing when viewing another's answer
          />
        ) : (
          <AnswerQuestionModal
            onClose={() => setSelectedQuestion(null)}
            questionId={selectedQuestion._id}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            onAnswerPosted={() => {
              fetchQuestions();
              setSelectedQuestion(null);
            }}
          />
        )
      )}
    </>
  );
};

export default QuestionAsked;
